var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { PatientAnalyticsEvent } from "healthex-common/analytics.dtos";
import { DocumentVectorizationStatus } from "healthex-common/document.retrieval.dtos";
import { dtcOrganizationId } from "healthex-common/organization.dtos";
import { StudyPeriodicDayAndWeekUnits, StudyTranslationLanguage, } from "healthex-common/study.dtos";
import { willNeverHappen } from "healthex-common/utils";
import { computed, observable, when } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { NOT_LOADED } from "../Infra/RemoteData";
import CaliforniaBillOfRightsPDF from "../Media/california-bill-of-rights.pdf";
import { FormModel } from "./FormModel";
import { PatientConsentBaseViewModel } from "./PatientConsentBaseViewModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { getReturnToPartnerLabel, getStudySectionImages, getTranslatedStudyText } from "./TranslationService";
import { getAnonymousUserId } from "../Logic/AuthManager";
const t = getObservableT("patients");
export function determineOrgId(loginUIManager, studyInfo) {
    return async () => {
        var _a;
        await when(() => loginUIManager.organization !== NOT_LOADED);
        if (studyInfo !== NOT_LOADED && studyInfo !== null) {
            const studyOrgId = (_a = studyInfo.addedByOrganization) === null || _a === void 0 ? void 0 : _a.id;
            if (studyOrgId) {
                return studyOrgId;
            }
        }
        if (loginUIManager.organization !== NOT_LOADED && loginUIManager.organization !== null) {
            return loginUIManager.organization.id;
        }
        return dtcOrganizationId;
    };
}
export class PatientConsentViewModel extends PatientConsentBaseViewModel {
    constructor() {
        super(...arguments);
        this.disposer = new Disposer();
        this.guidedConsentLanguageDialog = new SimpleDialogModel();
        this.guidedConsentLanguageFormModel = new FormModel({
            language: "en",
        }, ["language"]);
        this.hipaaAuthorizationFormDialogOpen = false;
    }
    run() {
        this.disposer.reaction(() => ({
            study: this.study,
            isInConsentFlow: this.isInConsentFlow,
            isGuidedConsent: this.isGuidedConsent,
            isWelcomePage: this.isWelcomePage,
            studyICFTranslationAvailableLanguages: this.patientConsentUIManager.studyICFTranslationAvailableLanguages,
        }), ({ study, isInConsentFlow, isGuidedConsent, isWelcomePage, studyICFTranslationAvailableLanguages }) => {
            if (study !== NOT_LOADED &&
                isInConsentFlow &&
                isGuidedConsent &&
                isWelcomePage &&
                studyICFTranslationAvailableLanguages.length > 1) {
                void this.openGuidedConsentLanguageDialog();
            }
        }, { fireImmediately: true, name: "PatientConsentViewModel_guidedConsentLanguageDialog" });
        this.disposer.reaction(() => { var _a; return (_a = this.patientConsentUIManager.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.language; }, (patientLanguage) => {
            if (patientLanguage) {
                this.guidedConsentLanguageFormModel.updateInput({
                    language: patientLanguage,
                });
            }
        }, { fireImmediately: true, name: "PatientConsentViewModel_guidedConsentLanguageFormModel" });
    }
    stop() {
        this.disposer.dispose();
    }
    async openGuidedConsentLanguageDialog() {
        var _a, _b;
        const patientLanguage = (_b = (_a = this.patientConsentUIManager.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.language) !== null && _b !== void 0 ? _b : "en";
        this.guidedConsentLanguageFormModel.updateInput({
            language: patientLanguage,
        });
        const confirmed = await this.guidedConsentLanguageDialog.showAndWait();
        if (!confirmed)
            return;
        const language = this.guidedConsentLanguageFormModel.input.language === StudyTranslationLanguage.es
            ? StudyTranslationLanguage.es
            : StudyTranslationLanguage.en;
        this.patientConsentUIManager.setInMemoryLanguage(language);
        this.closeGuidedConsentLanguageDialog();
    }
    get isPatientLoggedIn() {
        return this.patientConsentUIManager.isPatientLoggedIn;
    }
    get isDataUpdate() {
        return this.patientConsentUIManager.isDataUpdate;
    }
    get isIAS() {
        return this.patientConsentUIManager.isIAS;
    }
    get isWelcomePage() {
        const currentPage = this.patientConsentUIManager.currentPage;
        return currentPage === "ENROLLMENT_WELCOME";
    }
    get welcomeTitle() {
        if (this.isIAS) {
            return this.isHealthWalletEnabled ? t("Your Health Records,\nAll in One Place") : t("Get your HealthEx Unified Record");
        }
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return "";
        return this.getCustomizedText("welcomeTitle");
    }
    get supportingText() {
        if (this.isIAS) {
            return this.isHealthWalletEnabled
                ? t("Your HealthEx Wallet pulls your records from every provider into a single, unified view.")
                : t("Access your health records from your providers in one place. HealthEx covers 80% of providers and keeps adding more.");
        }
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return "";
        return this.getCustomizedText("welcomeText");
    }
    closeGuidedConsentLanguageDialog() {
        this.guidedConsentLanguageDialog.open = false;
    }
    get isGuidedConsent() {
        return this.patientConsentUIManager.session.guidedConsentPatientMRN !== null;
    }
    get currentLanguage() {
        return this.patientConsentUIManager.currentLanguage;
    }
    get studyInfo() {
        return this.patientConsentUIManager.studyInfo;
    }
    get patientFriendlyTitle() {
        return this.patientConsentUIManager.patientFriendlyTitle;
    }
    get studySponsor() {
        return this.patientConsentUIManager.studySponsor;
    }
    get patientFriendlySummary() {
        return this.patientConsentUIManager.patientFriendlySummary;
    }
    get irbApprovalStamp() {
        return this.patientConsentUIManager.irbApprovalStamp;
    }
    get studyPrimaryContactName() {
        return this.patientConsentUIManager.studyPrimaryContactName;
    }
    get studyPrimaryContactTitle() {
        return this.patientConsentUIManager.studyPrimaryContactTitle;
    }
    get futureUseOfDataText() {
        return this.patientConsentUIManager.futureUseOfDataText;
    }
    get isProvider() {
        return this.patientConsentUIManager.isProvider;
    }
    get studyPrimaryContactDetails() {
        const details = [];
        if (this.studyPrimaryContactName) {
            details.push(this.studyPrimaryContactName);
        }
        if (this.studyPrimaryContactTitle) {
            details.push(this.studyPrimaryContactTitle);
        }
        return details.join(", ");
    }
    get primaryContactEmail() {
        return this.patientConsentUIManager.primaryContactEmail;
    }
    get primaryContactPhone() {
        return this.patientConsentUIManager.primaryContactPhone;
    }
    get risksOfEnrollment() {
        return this.patientConsentUIManager.risksOfEnrollment;
    }
    get benefitsOfEnrollment() {
        return this.patientConsentUIManager.benefitsOfEnrollment;
    }
    get alternativesToParticipation() {
        return this.patientConsentUIManager.alternativesToParticipation;
    }
    get resourceScopes() {
        return this.patientConsentUIManager.resourceScopes;
    }
    get sensitivityScopes() {
        return this.patientConsentUIManager.sensitivityScopes;
    }
    get isFullRecordMode() {
        return this.patientConsentUIManager.isFullRecordMode;
    }
    get californiaBillOfRightsUrl() {
        if (this.isPreview)
            return null;
        return CaliforniaBillOfRightsPDF;
    }
    get isPDDEGeneralDataUse() {
        return this.patientConsentUIManager.isPDDAGeneralDataUse;
    }
    get isPDDAStudy() {
        return this.patientConsentUIManager.isPDDAStudy;
    }
    get pddaHealthRecordsUpdateFrequencyText() {
        const updateFrequency = this.patientConsentUIManager.pddaHealthRecordsUpdateFrequency;
        if (!updateFrequency)
            return null;
        switch (updateFrequency.unit) {
            case StudyPeriodicDayAndWeekUnits.DAY:
                return t("daysWithCount", { count: updateFrequency.frequency });
                break;
            case StudyPeriodicDayAndWeekUnits.WEEK:
                return t("weeksWithCount", { count: updateFrequency.frequency });
                break;
        }
        return willNeverHappen(updateFrequency.unit);
    }
    get locatingPDDERecords() {
        return this.patientConsentUIManager.locatingPDDERecords;
    }
    get canAddLocations() {
        return this.patientConsentUIManager.canAddLocations;
    }
    get zeroLocationsFound() {
        return this.patientConsentUIManager.zeroLocationsFound;
    }
    get zeroLocationsText() {
        if (!this.isPDDEGeneralDataUse)
            return null;
        if (!this.zeroLocationsFound)
            return null;
        if (this.isDataUpdate) {
            return t("At this time, no records could be retrieved.\n\nIf your records become available, we may reach out again.\n\nYou can review and change who your records are shared with from the HealthEx portal at any time.");
        }
        return t("You’ve agreed to share your records with {{ orgName }}. At this time, no records could be retrieved.\n\nIf your records become available, we may reach out again.\n\nYou can review and change who your records are shared with from the HealthEx portal at any time.", { orgName: this.organizationName });
    }
    get pddaThankYouText() {
        if (!this.isPDDAStudy)
            return "";
        const frequencyText = this.pddaHealthRecordsUpdateFrequencyText;
        if (!frequencyText) {
            return t("Your request has been successfully submitted. You will receive a confirmation message once your health information is ready to be downloaded.");
        }
        else {
            return t("Thank you for completing this request. You will be contacted again in approximately {{ frequencyText }}, based on the schedule for the study. We appreciate your participation and contribution to:", { frequencyText });
        }
    }
    get getSectionImages() {
        return (sectionName, additionalSectionId) => {
            if (this.study === NOT_LOADED)
                return null;
            return getStudySectionImages(this.study, this.patientConsentUIManager.currentLanguage, sectionName, additionalSectionId);
        };
    }
    get showFooterLanguagePickerForWelcomeScreen() {
        const shouldCheckForProjectTranslations = !this.isIAS;
        if (shouldCheckForProjectTranslations && this.patientConsentUIManager.studyICFTranslationAvailableLanguages.length <= 1) {
            return false;
        }
        return this.isPDDEGeneralDataUse && this.isWelcomePage && !this.isPreview;
    }
    get pddeGDUWelcomeConfirmButtonText() {
        return t("Get Started");
    }
    async confirmConsent(allowNavigationAfterConsentConfirmation) {
        if (this.isPreview) {
            this.goToNextPage();
            return;
        }
        await this.patientConsentUIManager.confirmConsent(allowNavigationAfterConsentConfirmation);
    }
    showPopup(route) {
        this.patientConsentUIManager.showPopup(route);
    }
    goToPage(route) {
        if (!route) {
            return;
        }
        this.patientConsentUIManager.goToPage(route);
    }
    goBack() {
        this.patientConsentUIManager.goToPreviousPage();
    }
    async declineConsent() {
        var _a;
        await this.patientConsentUIManager.declineConsent();
        const identity = (_a = this.authManager.id) !== null && _a !== void 0 ? _a : undefined;
        heap.addEventProperties({ globalNewPatient: "false", projectNewPatient: "false" });
        void this.analyticsService.trackHeapEvent({
            event: PatientAnalyticsEvent.PATIENT_FLOW_COMPLETED,
            identity,
            properties: {
                ...getHeapGlobalEventProperties(),
                completionReason: "Consent declined",
                dataUpdateType: this.patientConsentUIManager.dataUpdateType,
            },
        });
    }
    done() {
        if (this.isPreview)
            return;
        this.patientConsentUIManager.done();
    }
    get healthWalletExitButtonText() {
        return this.isHealthWalletEnabledForNavigation ? t("Open Your Wallet") : t("Go to HealthEx");
    }
    showRequest() {
        this.patientConsentUIManager.showPopup("ENROLLMENT_REQUEST_DETAILS_POPUP");
    }
    getCustomizedText(key) {
        if (this.studyInfo !== NOT_LOADED && this.studyInfo !== null) {
            return getTranslatedStudyText(this.studyInfo, key, this.patientConsentUIManager.currentLanguage) || "";
        }
        return "";
    }
    get retrieval() {
        return this.patientConsentUIManager.retrieval;
    }
    get initialLocationsNeedingAuthentication() {
        return this.patientConsentUIManager.initialLocationsNeedingAuthentication;
    }
    get isAIFlow() {
        return this.patientConsentUIManager.isAIFlow;
    }
    get isOAuthFlow() {
        return this.patientConsentUIManager.isOAuthFlow;
    }
    get hasHostedRedirectUrl() {
        return this.patientConsentUIManager.hasHostedRedirectUrl;
    }
    get canRedirectExternally() {
        return this.patientConsentUIManager.canRedirectExternally;
    }
    get hostedOrganizationName() {
        return this.organizationName || t("the application");
    }
    get returnToPartnerLabel() {
        const study = this.studyInfo === NOT_LOADED ? null : this.studyInfo;
        return getReturnToPartnerLabel(study, this.patientConsentUIManager.currentLanguage);
    }
    get hostedFlowButtonText() {
        const label = this.returnToPartnerLabel;
        if (label)
            return label;
        return this.hostedFlowDefaultButtonText;
    }
    get hostedFlowDefaultButtonText() {
        return t("Go back to {{ orgName }}", { orgName: this.hostedOrganizationName });
    }
    async onHostedFlowRedirect() {
        if (this.isPreview)
            return;
        await this.patientConsentUIManager.handleRedirectForHostedFlow("declined");
    }
    get isVectorizationComplete() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.COMPLETE;
    }
    get faqButtonText() {
        if (this.isAIFlow)
            return t("View Connector Details");
        if (this.isIAS)
            return t("View FAQs");
        return t("View Request Details");
    }
    getCommonHeapAttributes() {
        var _a, _b;
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        const identity = (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined;
        const isDataUpdate = this.patientConsentUIManager.isDataUpdate;
        const isLoggedIn = this.authManager.isLoggedIn;
        return {
            identity,
            user_id: userId,
            properties: { ...getHeapGlobalEventProperties(), isDataUpdate, isLoggedIn },
        };
    }
}
__decorate([
    computed
], PatientConsentViewModel.prototype, "isPatientLoggedIn", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isDataUpdate", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isIAS", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isWelcomePage", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "welcomeTitle", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "supportingText", null);
__decorate([
    observable
], PatientConsentViewModel.prototype, "hipaaAuthorizationFormDialogOpen", void 0);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isGuidedConsent", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "currentLanguage", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "studyInfo", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "patientFriendlyTitle", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "studySponsor", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "patientFriendlySummary", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "irbApprovalStamp", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "studyPrimaryContactName", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "studyPrimaryContactTitle", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "futureUseOfDataText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isProvider", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "studyPrimaryContactDetails", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "primaryContactEmail", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "primaryContactPhone", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "risksOfEnrollment", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "benefitsOfEnrollment", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "alternativesToParticipation", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "resourceScopes", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "sensitivityScopes", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isFullRecordMode", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "californiaBillOfRightsUrl", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isPDDEGeneralDataUse", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isPDDAStudy", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "pddaHealthRecordsUpdateFrequencyText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "locatingPDDERecords", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "canAddLocations", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "zeroLocationsFound", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "zeroLocationsText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "pddaThankYouText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "getSectionImages", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "showFooterLanguagePickerForWelcomeScreen", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "pddeGDUWelcomeConfirmButtonText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "healthWalletExitButtonText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "retrieval", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "initialLocationsNeedingAuthentication", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isAIFlow", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "canRedirectExternally", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "hostedOrganizationName", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "returnToPartnerLabel", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "hostedFlowButtonText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "hostedFlowDefaultButtonText", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "isVectorizationComplete", null);
__decorate([
    computed
], PatientConsentViewModel.prototype, "faqButtonText", null);
